import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { ConsentService } from 'src/app/services/consent.service';

@Component({
  selector: 'app-cookies-banner',
  templateUrl: './cookies-banner.component.html',
  styleUrls: ['./cookies-banner.component.scss'],
  host: {
    class: 'cookies-banner',
    '[class.is-dismissible]': 'dismissible',
  },
})
export class CookiesBannerComponent implements OnInit, AfterViewInit {
  @Output() accepted = new EventEmitter<void>();
  /** Emitted when the user dismisses a reopened banner without changing it. */
  @Output() closed = new EventEmitter<void>();

  @ViewChild('banner') private bannerRef?: ElementRef<HTMLElement>;

  showPreferences = false;
  // Pre-tick analytics (defensible — aggregate events, no direct PII).
  // sessionRecording must NOT be pre-ticked: CNIL, ICO and ANPD's 2023
  // cookie guidance forbid pre-ticked boxes for invasive categories.
  // Pre-ticking would invalidate consent under LGPD Art. 8 §3 (specific)
  // and Art. 9 (informed). Users can still opt in by clicking the
  // checkbox or via the "Aceitar todos" button.
  analyticsChecked = true;
  sessionRecordingChecked = false;
  // True when the banner is reopened by an already-decided user (via
  // "Gerenciar cookies"). Adds a close affordance so they can back out
  // without re-deciding; on first visit the banner is NOT dismissible —
  // a choice is required.
  dismissible = false;
  // Locks the action buttons after the first click so double-clicks (or
  // a slow change-detection cycle before the parent unmounts us) don't
  // fire `accept` twice and don't let the user pick a different option
  // mid-submit.
  submitted = false;

  constructor(private consent: ConsentService) {}

  ngOnInit(): void {
    // When reopened by an already-decided user, mirror their current
    // choice instead of the first-visit defaults, allow dismissing, and
    // open straight into the granular view — they clicked "Gerenciar
    // cookies" to review categories, not to hit a shortcut button.
    if (this.consent.hasDecided) {
      this.dismissible = true;
      this.showPreferences = true;
      this.analyticsChecked = this.consent.has('analytics');
      this.sessionRecordingChecked = this.consent.has('sessionRecording');
    }
  }

  ngAfterViewInit(): void {
    // Reopened as the consent-management dialog: pull focus into the banner
    // so keyboard / screen-reader users aren't stranded on the now-destroyed
    // "Gerenciar cookies" trigger, and the dialog role + label is announced.
    // First-visit banner stays passive — no focus steal on every page load.
    if (this.dismissible) {
      this.bannerRef?.nativeElement.focus();
    }
  }

  close(): void {
    this.closed.emit();
  }

  togglePreferences(): void {
    if (this.submitted) return;
    this.showPreferences = !this.showPreferences;
  }

  acceptAll(): void {
    this.submit(() => this.consent.acceptAll());
  }

  acceptEssentialOnly(): void {
    this.submit(() => this.consent.essentialOnly());
  }

  savePreferences(): void {
    this.submit(() => {
      const categories: Array<'analytics' | 'sessionRecording'> = [];
      if (this.analyticsChecked) categories.push('analytics');
      if (this.sessionRecordingChecked) categories.push('sessionRecording');
      this.consent.accept(categories);
    });
  }

  private submit(action: () => void): void {
    if (this.submitted) return;
    this.submitted = true;
    action();
    this.accepted.emit();
  }
}
